
export function GlobalPresenceSection() {
  const regions = [
    {
      name: "Middle East",
      description: "Strategic partnerships with leading oil & gas companies",
      focus: "Upstream exploration and reservoir optimization",
      icon: "ri-oil-line",
      position: { top: "45%", left: "58%" }
    },
    {
      name: "Asia-Pacific", 
      description: "Technology innovation hub and regional headquarters",
      focus: "AI development and high-tech solutions",
      icon: "ri-cpu-line",
      position: { top: "55%", left: "75%" }
    },
    {
      name: "North America",
      description: "Advanced technology integration and market expansion",
      focus: "Cross-industry technology applications",
      icon: "ri-rocket-line",
      position: { top: "35%", left: "25%" }
    }
  ];

  return (
    <section id="global" className="py-24 bg-gradient-to-b from-slate-900/50 to-slate-800/50">
      <div className="container mx-auto px-6">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-6">
            Global <strong className="text-blue-400">Presence</strong>
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto mb-8">
            Strategic operations across three key regions, delivering localized excellence worldwide
          </p>
          
          {/* Singapore Headquarters Highlight */}
          <div className="inline-flex items-center gap-4 bg-blue-600/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-8 py-4">
            <i className="ri-building-line text-blue-400 text-xl"></i>
            <span className="text-white font-medium">
              Headquarters: Singapore • Gateway to Global Markets
            </span>
          </div>
        </div>

        {/* Interactive World Map - Full Width */}
        <div className="mb-16">
          <div className="relative w-full h-80 bg-gradient-to-br from-blue-900/20 to-blue-800/30 rounded-2xl overflow-hidden border border-blue-500/20">
            {/* World Map Background */}
            <div 
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: `url('https://public.readdy.ai/ai/img_res/69a05c8fc29c435e883bc70cadfeacb9.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            
            {/* Pulse Points */}
            {/* Middle East */}
            <div className="absolute" style={{ left: '55%', top: '35%' }}>
              <div className="absolute w-16 h-16 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-blue-400/40 animate-ping"></div>
              <div className="absolute w-10 h-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/30 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
              <div className="absolute w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white animate-pulse" style={{ animationDelay: '1s' }}></div>
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs text-blue-300 font-medium whitespace-nowrap">Middle East</div>
            </div>

            {/* Asia Pacific */}
            <div className="absolute" style={{ left: '75%', top: '45%' }}>
              <div className="absolute w-16 h-16 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-blue-400/40 animate-ping" style={{ animationDelay: '0.3s' }}></div>
              <div className="absolute w-10 h-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/30 animate-pulse" style={{ animationDelay: '0.8s' }}></div>
              <div className="absolute w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white animate-pulse" style={{ animationDelay: '1.3s' }}></div>
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs text-blue-300 font-medium whitespace-nowrap">Asia Pacific</div>
            </div>

            {/* North America */}
            <div className="absolute" style={{ left: '25%', top: '30%' }}>
              <div className="absolute w-16 h-16 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-blue-400/40 animate-ping" style={{ animationDelay: '0.7s' }}></div>
              <div className="absolute w-10 h-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/30 animate-pulse" style={{ animationDelay: '1.2s' }}></div>
              <div className="absolute w-4 h-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white animate-pulse" style={{ animationDelay: '1.7s' }}></div>
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs text-blue-300 font-medium whitespace-nowrap">North America</div>
            </div>

            {/* Connection Lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              <defs>
                <linearGradient id="connectionGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(59, 130, 246, 0.6)" />
                  <stop offset="50%" stopColor="rgba(147, 197, 253, 0.8)" />
                  <stop offset="100%" stopColor="rgba(59, 130, 246, 0.6)" />
                </linearGradient>
              </defs>
              <line 
                x1="55%" y1="35%" 
                x2="75%" y2="45%" 
                stroke="url(#connectionGradient1)" 
                strokeWidth="2" 
                strokeDasharray="8,4"
                className="animate-pulse"
                style={{ animationDelay: '2s' }}
              />
              <line 
                x1="55%" y1="35%" 
                x2="25%" y2="30%" 
                stroke="url(#connectionGradient1)" 
                strokeWidth="2" 
                strokeDasharray="8,4"
                className="animate-pulse"
                style={{ animationDelay: '2.5s' }}
              />
              <line 
                x1="75%" y1="45%" 
                x2="25%" y2="30%" 
                stroke="url(#connectionGradient1)" 
                strokeWidth="2" 
                strokeDasharray="8,4"
                className="animate-pulse"
                style={{ animationDelay: '3s' }}
              />
            </svg>
          </div>
        </div>

        {/* Content Grid Below Map */}
        <div className="grid lg:grid-cols-2 gap-16 items-start mb-20">
          {/* Left: Description and Stats */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-light text-white mb-4">
                Serving Clients <strong className="text-blue-400">Globally</strong>
              </h3>
              <p className="text-lg text-white/70 leading-relaxed">
                Our strategic presence across three major regions enables us to deliver 
                localized expertise while maintaining global standards of excellence.
              </p>
            </div>

            {/* Key Statistics */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-2">3</div>
                <div className="text-sm text-white/70">Key Regions</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-2">24/7</div>
                <div className="text-sm text-white/70">Global Support</div>
              </div>
            </div>
          </div>

          {/* Right: Quick Region Overview */}
          <div className="space-y-4">
            <h4 className="text-xl font-light text-white mb-6">
              Regional <strong className="text-blue-400">Focus Areas</strong>
            </h4>
            {regions.map((region, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl hover:bg-white/10 transition-all duration-300">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <i className={`${region.icon} text-lg text-blue-400`}></i>
                </div>
                <div className="flex-1">
                  <h5 className="text-white font-semibold mb-1">{region.name}</h5>
                  <p className="text-sm text-blue-300">{region.focus}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Regional Information */}
        <div>
          <h3 className="text-2xl font-light text-white text-center mb-12">
            Regional <strong className="text-blue-400">Expertise</strong>
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {regions.map((region, index) => (
              <div 
                key={index}
                className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 hover:transform hover:scale-105"
              >
                <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-500/30 transition-colors duration-300">
                  <i className={`${region.icon} text-2xl text-blue-400`}></i>
                </div>
                
                <h3 className="text-2xl font-semibold text-white mb-4">
                  {region.name}
                </h3>
                
                <p className="text-white/70 mb-4 leading-relaxed">
                  {region.description}
                </p>
                
                <div className="border-t border-white/10 pt-4">
                  <p className="text-sm text-blue-300 font-medium">
                    Focus: {region.focus}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: .5;
          }
        }
      `}</style>
    </section>
  );
}